import config from '@/config';
import { FetchFgpGameResponse } from '@/features/game/types';
import { GenericProvider, Provider, RGI } from '@/features/game-context/types';
import { isGenericProvider } from '@/features/game-window-integration/providers/generic-provider/utils.ts';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { FgpAristocrat } from '@/features/providers/aristocrat/types';
import { FgpAwager } from '@/features/providers/awager/types';
import { FgpDgc } from '@/features/providers/dgc/types';
import { FgpEveri } from '@/features/providers/everi/types';
import { FgpEvolution } from '@/features/providers/evolution/types';
import { FgpFusion } from '@/features/providers/fusion/types';
import { FgpGenericProvider } from '@/features/providers/generic-provider/types';
import { FgpIGT } from '@/features/providers/igt/types';
import { FgpLnw } from '@/features/providers/lnw/types';
import { FgpPlaytech } from '@/features/providers/playtech/types';
import { FgpPragmatic } from '@/features/providers/pragmatic/types';
import {
  FgpGameData,
  FgpLaunchData,
  ProviderToDataMap,
} from '@/features/providers/types';

const providerToDataMap: ProviderToDataMap<RGI.FGP> = {
  [Provider.IGT]: (response) => ({
    launchDetails: (response.data as FgpIGT).launchDetails,
    requestId: (response.data as FgpIGT).requestId,
  }),
  [Provider.LNW]: (response) => ({
    launchDetails: (response.data as FgpLnw).launchDetails,
    requestId: (response.data as FgpLnw).requestId,
    lobbyUrl: config.lobbyUrl,
  }),
  [Provider.EVOLUTION]: (response) => ({
    launchDetails: (response.data as FgpEvolution).launchDetails,
    requestId: (response.data as FgpEvolution).requestId,
  }),
  [Provider.EVERI]: (response) => ({
    launchDetails: (response.data as FgpEveri).launchDetails,
    requestId: (response.data as FgpEveri).requestId,
  }),
  [Provider.DGC]: (response) => ({
    launchDetails: (response.data as FgpDgc).launchDetails,
    requestId: (response.data as FgpDgc).requestId,
  }),
  [Provider.HIGH5]: () => undefined,
  [Provider.SPINGAMING]: () => undefined,
  [Provider.REALMS]: () => undefined,
  [Provider.EZUGI]: () => undefined,
  [Provider.NETENT]: () => undefined,
  [Provider.ARISTOCRAT]: (response) => ({
    launchDetails: (response.data as FgpAristocrat).launchDetails,
    requestId: (response.data as FgpAristocrat).requestId,
  }),
  [Provider.PLAYTECH]: (response) => ({
    launchDetails: (response.data as FgpPlaytech).launchDetails,
    requestId: (response.data as FgpPlaytech).requestId,
  }),
  [Provider.PLAYTECH_POKER]: (response) => ({
    launchDetails: (response.data as FgpPlaytech).launchDetails,
    requestId: (response.data as FgpPlaytech).requestId,
  }),
  [Provider.PRAGMATIC]: (response) => ({
    launchDetails: (response.data as FgpPragmatic).launchDetails,
    requestId: (response.data as FgpPragmatic).requestId,
  }),
  [Provider.AWAGER]: (response) => ({
    launchDetails: (response.data as FgpAwager).launchDetails,
    requestId: (response.data as FgpAwager).requestId,
  }),
  [Provider.FUSION]: (response) => ({
    launchDetails: (response.data as FgpFusion).launchDetails,
    requestId: (response.data as FgpFusion).requestId,
  }),
};

export const mapFgpResponseToGameData = (
  response: FetchFgpGameResponse<FgpLaunchData>,
  provider: Provider | GenericProvider,
): FgpGameData | undefined => {
  if (!providerToDataMap[provider] && isGenericProvider(provider)) {
    providerToDataMap[provider] = (res) => ({
      launchDetails: (res.data as FgpGenericProvider).launchDetails,
      requestId: (res.data as FgpGenericProvider).requestId,
    });
  }
  const providerHandler = providerToDataMap[provider];

  if (providerHandler) {
    const data = {
      ...providerHandler(response, provider),
      source: RGI.FGP,
      provider,
    } as FgpGameData;

    if (data) {
      Logger.rumAction({
        name: 'Provider data mapped successfully',
        useCase: DdEventCategory.FetchFgp,
        eventType: DdEventType.Success,
      });
    } else {
      Logger.rumError({
        name: 'Failed to map provider data',
        useCase: DdEventCategory.FetchFgp,
        eventType: DdEventType.Error,
        context: {
          data: response,
          provider,
        },
      });
    }

    return data;
  }

  Logger.rumError({
    name: 'No provider mapper found',
    useCase: DdEventCategory.FetchFgp,
    eventType: DdEventType.Error,
    context: {
      provider,
    },
  });

  return undefined;
};
